import { defineStore } from 'pinia';
import { ref, computed, Ref } from 'vue';
import { User, UserOnboardingStep, UserResumeOrigin, UserProgram } from '@addviseo/shared/types';
import {
  ProgramStates,
  getProgramStateFromUserPrograms,
  type ProgramState,
  UserProgramStatuses,
} from '@addviseo/shared/constants';
import { canShareProfile, canFeatureResume } from '@addviseo/shared/utils/user-resume.js';
import {
  validateOnboardingStep as validateOnboardingStepApi,
  updateProfileShared as updateProfileSharedApi,
  reactivateAutoSharing as reactivateAutoSharingApi,
} from '@/api/user';
import { getMe, getUserProfessionalExperiences } from '@/api/user';
import router from '@/router';
import { getUserResumes } from '@/api/resume';
import { getUserSkills } from '@/api/skills';
import useToaster from '@/composables/useToaster';

interface UserStore extends User {
  initials: string;
  fullName: string;
  onboarding: {
    started: boolean;
    step: UserOnboardingStep;
  };
  hasParsedInternalResume: boolean;
  canShareProfile: boolean;
  canFeatureResume: boolean;
}

export const useUserStore = defineStore('user', () => {
  const _user = ref<User | null>(null);
  const isLoading: Ref<boolean> = ref(false);
  const toastr = useToaster();
  const user = computed<UserStore | null>(() => {
    if (!_user.value) return null;

    const allResumes = _user.value.resumes ?? [];
    return {
      ..._user.value,
      initials: _user.value.firstName?.charAt(0)?.toUpperCase() || '',
      fullName: `${_user.value.firstName} ${_user.value.lastName}`,
      onboarding: {
        started: _user.value.onboardingStep !== UserOnboardingStep.NOT_STARTED,
        step: _user.value.onboardingStep,
      },
      // Expose only non-soft-deleted resumes for display. The raw list
      // (including deleted) is still available via hasParsedInternalResume,
      // which must account for history to keep the "parse once" invariant.
      resumes: allResumes.filter((resume) => !resume.deletedAt),
      hasParsedInternalResume: allResumes.some(
        (resume) => resume.origin === UserResumeOrigin.INTERNAL && resume.parsed,
      ),
      canShareProfile: canShareProfile(allResumes),
      canFeatureResume: canFeatureResume(_user.value),
    };
  });
  const programState = computed<ProgramState>(() =>
    getProgramStateFromUserPrograms(_user.value?.userPrograms ?? []),
  );

  /** True when program is in progress or extended. */
  const hasActiveProgram = computed(() => programState.value === ProgramStates.ONGOING);

  const activeProgram = computed<UserProgram | null>(
    () =>
      _user.value?.userPrograms?.find(
        (program) =>
          program.status === UserProgramStatuses.IN_PROGRESS ||
          program.status === UserProgramStatuses.EXTENDED,
      ) ?? null,
  );

  const handleError = (error: any, message: string) => {
    // Don't toast errors when redirecting to login
    if (!(error as any)?.response || (error as any)?.response?.status === 401) {
      return;
    }

    toastr.error(message);
  };

  const fetchUser = async () => {
    isLoading.value = true;
    try {
      const data = await getMe();
      _user.value = data;
    } catch (error) {
      handleError(error, "Une erreur est survenue lors de la récupération de l'utilisateur.");
      _user.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchUserProfessionalExperiences = async () => {
    try {
      if (_user.value) {
        _user.value.professionalExperiences = await getUserProfessionalExperiences();
      }
    } catch (error) {
      handleError(
        error,
        'Une erreur est survenue lors de la récupération des expériences professionnelles.',
      );
    }
  };

  const fetchUserResumes = async () => {
    try {
      if (_user.value) {
        _user.value.resumes = await getUserResumes();
      }
    } catch (error) {
      handleError(error, 'Une erreur est survenue lors de la récupération des CV.');
    }
  };

  const fetchUserSkills = async () => {
    try {
      if (_user.value) {
        _user.value.skills = await getUserSkills();
      }
    } catch (error) {
      handleError(error, 'Une erreur est survenue lors de la récupération des compétences.');
    }
  };

  const redirectIfOnboardingStarted = () => {
    if (user.value && user.value.onboardingStep > UserOnboardingStep.NOT_STARTED) {
      void router.push({ name: 'Onboarding' });
    }
  };

  const redirectIfOnboardingNotStarted = () => {
    if (user.value && user.value.onboardingStep === UserOnboardingStep.NOT_STARTED) {
      void router.push({ name: 'Home' });
    }
  };

  const validateOnboardingStep = async (step: UserOnboardingStep) => {
    const { step: newStep } = await validateOnboardingStepApi(step);
    if (_user.value) {
      _user.value.onboardingStep = newStep;
    }

    if (newStep === UserOnboardingStep.COMPLETED) {
      void router.push({ name: 'HomeAppointment' });
    }

    return newStep;
  };

  const setProfileShared = async (profileShared: boolean) => {
    if (!_user.value) return;

    const previousShared = _user.value.profileShared;
    const previousManuallyDisabled = _user.value.profileSharingManuallyDisabled;
    _user.value.profileShared = profileShared;
    _user.value.profileSharingManuallyDisabled = !profileShared;

    try {
      await updateProfileSharedApi(profileShared);
    } catch (error) {
      _user.value.profileShared = previousShared;
      _user.value.profileSharingManuallyDisabled = previousManuallyDisabled;
      throw error;
    }
  };

  const reactivateAutoSharing = async () => {
    if (!_user.value) return;

    const previous = _user.value.profileSharingManuallyDisabled;
    _user.value.profileSharingManuallyDisabled = false;

    try {
      await reactivateAutoSharingApi();
    } catch (error) {
      _user.value.profileSharingManuallyDisabled = previous;
      throw error;
    }
  };

  return {
    user,
    programState,
    hasActiveProgram,
    isLoading,
    activeProgram,
    fetchUser,
    redirectIfOnboardingStarted,
    redirectIfOnboardingNotStarted,
    validateOnboardingStep,
    fetchUserProfessionalExperiences,
    fetchUserResumes,
    fetchUserSkills,
    setProfileShared,
    reactivateAutoSharing,
  };
});
